import React, { useState } from "react";
import { motion } from "framer-motion";
import {
  Box,
  Button,
  Chip,
  Container,
  Grid,
  Stack,
  Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import SecurityIcon from "@mui/icons-material/Security";
import SpeedIcon from "@mui/icons-material/Speed";
import IntegrationInstructionsIcon from "@mui/icons-material/IntegrationInstructions";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import SimpleEmployeePage from "./SimpleEmployeePage";
import CustomAlert from "../../components/ui/CustomAlert";

const ACCENT = "#1d4ed8";
const ACCENT_LIGHT = "#2563eb";
const GRADIENT =
  "linear-gradient(145deg, #0f172a 0%, #1e3a8a 35%, #1d4ed8 70%, #2563eb 100%)";

const features = [
  { icon: SmartToyIcon, label: "Agentes IA autonomos" },
  { icon: SecurityIcon, label: "Permissoes granulares" },
  { icon: SpeedIcon, label: "Limite de acoes/hora" },
  { icon: IntegrationInstructionsIcon, label: "Integracao" },
];

const pageVariants = {
  initial: { opacity: 0 },
  in: { opacity: 1, transition: { duration: 0.3 } },
  out: { opacity: 0, transition: { duration: 0.3 } },
};

const stagger = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1, delayChildren: 0.05 },
  },
};

const riseUp = {
  hidden: { opacity: 0, y: 30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { type: "spring", stiffness: 200, damping: 22 },
  },
};

const SimpleEmployee = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [alertOpen, setAlertOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertSeverity, setAlertSeverity] = useState("success");

  const showAlert = (message, severity = "success") => {
    setAlertMessage(message);
    setAlertSeverity(severity);
    setAlertOpen(true);
    setTimeout(() => setAlertOpen(false), 5000);
  };

  const closeAlert = () => setAlertOpen(false);

  const scrollToContent = () => {
    document
      .getElementById("employee-content")
      ?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <motion.div
      initial="initial"
      animate="in"
      exit="out"
      variants={pageVariants}
    >
      {/* ─── HERO ─── */}
      <Box
        sx={{
          position: "relative",
          overflow: "hidden",
          pt: { xs: 6, md: 10 },
          pb: { xs: 8, md: 14 },
          background: GRADIENT,
        }}
      >
        {/* Grid pattern */}
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            opacity: 0.03,
            backgroundImage:
              "linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)",
            backgroundSize: "48px 48px",
          }}
        />

        {/* Glow */}
        <Box
          component={motion.div}
          animate={{ y: [0, -12, 0], x: [0, 8, 0] }}
          transition={{ duration: 12, repeat: Infinity, repeatType: "reverse" }}
          sx={{
            position: "absolute",
            width: 450,
            height: 450,
            borderRadius: "50%",
            background: `radial-gradient(circle, ${alpha(
              "#60a5fa",
              0.12
            )} 0%, transparent 65%)`,
            top: -150,
            right: -100,
          }}
        />

        <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
          <motion.div variants={stagger} initial="hidden" animate="visible">
            {/* Badge */}
            <motion.div variants={riseUp}>
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                justifyContent="center"
                sx={{ mb: 2.5 }}
              >
                <Chip
                  label="ASSISTENTE SIMPLES"
                  size="small"
                  sx={{
                    backgroundColor: alpha("#fff", 0.15),
                    color: "#fff",
                    fontWeight: 700,
                    fontSize: "0.65rem",
                    letterSpacing: "2px",
                    backdropFilter: "blur(8px)",
                    border: `1px solid ${alpha("#fff", 0.2)}`,
                  }}
                />
              </Stack>
            </motion.div>

            {/* Title */}
            <motion.div variants={riseUp}>
              <Typography
                variant={isMobile ? "h4" : "h2"}
                fontWeight={800}
                align="center"
                sx={{
                  color: "#fff",
                  mb: 2,
                  letterSpacing: "-0.02em",
                  lineHeight: 1.15,
                }}
              >
                Agentes IA que trabalham
                <br />
                <Box
                  component="span"
                  sx={{
                    background: "linear-gradient(90deg, #60a5fa, #a78bfa)",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                  }}
                >
                  por voce
                </Box>
              </Typography>
            </motion.div>

            <motion.div variants={riseUp}>
              <Typography
                variant="h6"
                align="center"
                fontWeight={400}
                sx={{
                  color: alpha("#fff", 0.6),
                  maxWidth: 520,
                  mx: "auto",
                  mb: 5,
                  lineHeight: 1.6,
                  fontSize: { xs: "0.9rem", md: "1rem" },
                }}
              >
                Cadastre agentes com permissoes personalizadas, instrucoes
                especificas e limites de seguranca. Automacao inteligente.
              </Typography>
            </motion.div>

            {/* Feature pills */}
            <motion.div variants={stagger}>
              <Grid
                container
                spacing={2}
                justifyContent="center"
                sx={{ mb: 4 }}
              >
                {features.map((f, i) => {
                  const Icon = f.icon;
                  return (
                    <Grid item key={i}>
                      <motion.div variants={riseUp}>
                        <Chip
                          icon={
                            <Icon
                              sx={{
                                fontSize: 16,
                                color: `${alpha("#60a5fa", 0.9)} !important`,
                              }}
                            />
                          }
                          label={f.label}
                          sx={{
                            backgroundColor: alpha("#fff", 0.08),
                            color: "#fff",
                            fontWeight: 500,
                            fontSize: "0.78rem",
                            border: `1px solid ${alpha("#fff", 0.12)}`,
                            backdropFilter: "blur(4px)",
                            px: 0.5,
                            height: 34,
                          }}
                        />
                      </motion.div>
                    </Grid>
                  );
                })}
              </Grid>
            </motion.div>

            {/* Scroll CTA */}
            <motion.div variants={riseUp}>
              <Stack alignItems="center">
                <Button
                  onClick={scrollToContent}
                  endIcon={<ArrowDownwardIcon sx={{ fontSize: 16 }} />}
                  sx={{
                    color: alpha("#fff", 0.6),
                    textTransform: "none",
                    fontWeight: 500,
                    "&:hover": {
                      color: "#fff",
                      backgroundColor: alpha("#fff", 0.06),
                    },
                  }}
                >
                  Ver agentes
                </Button>
              </Stack>
            </motion.div>
          </motion.div>
        </Container>

        {/* Separator */}
        <Box
          sx={{
            position: "absolute",
            bottom: -1,
            left: 0,
            right: 0,
            lineHeight: 0,
          }}
        >
          <svg
            viewBox="0 0 1440 80"
            preserveAspectRatio="none"
            style={{ width: "100%", height: 50, display: "block" }}
          >
            <path
              d="M0 80L1440 0V80H0Z"
              fill={theme.palette.background.default}
            />
          </svg>
        </Box>
      </Box>

      {/* ─── CONTENT ─── */}
      <Box
        id="employee-content"
        maxWidth="xl"
        sx={{ py: { xs: 2, md: 4 }, px: { xs: 1, md: 8 } }}
      >
        <CustomAlert
          open={alertOpen}
          message={alertMessage}
          severity={alertSeverity}
          onClose={closeAlert}
        />

        <SimpleEmployeePage showAlert={showAlert} />
      </Box>
    </motion.div>
  );
};

export default SimpleEmployee;
